// ================================================================
// PROPRIETARY & CONFIDENTIAL — Miskulin Technologies Inc.
// Forge Casino — KYC Verification Page
// Identity verification for withdrawals (AML/FINTRAC compliance)
// ================================================================
import React, { useState } from 'react';
import { useCasino } from '../context/CasinoShell';

export default function KYCVerifyPage() {
  const { kycStatus, setKycStatus } = useCasino();
  const [formData, setFormData] = useState({
    fullName: '',
    dateOfBirth: '',
    address: '',
    city: '',
    province: '',
    postalCode: '',
    country: 'Canada',
    idType: '',
    idNumber: '',
  });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // In production, this would submit to a KYC provider (Jumio, Onfido, etc.)
    // For now, simulate verification
    setSubmitted(true);
    setKycStatus('verified');
    localStorage.setItem('forgecasino_kyc', JSON.stringify({
      status: 'verified',
      verifiedAt: new Date().toISOString(),
      name: formData.fullName,
    }));
  };

  if (kycStatus === 'verified' || submitted) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.successIcon}>✅</div>
          <h2 style={styles.successTitle}>IDENTITY VERIFIED</h2>
          <p style={styles.successText}>
            Your identity has been verified. You can now withdraw $FORGE from your casino wallet.
          </p>
          <div style={styles.complianceNote}>
            <span style={styles.shield}>🛡️</span>
            KYC verification is required by FINTRAC (Financial Transactions and Reports Analysis Centre of Canada)
            for anti-money laundering compliance.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.header}>
          <span style={{ fontSize: 36 }}>🛡️</span>
          <h1 style={styles.title}>IDENTITY VERIFICATION</h1>
          <p style={styles.subtitle}>Required for withdrawals — AML/KYC Compliance</p>
        </div>

        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.inputGroup}>
            <label style={styles.label}>FULL LEGAL NAME</label>
            <input
              style={styles.input}
              type="text"
              value={formData.fullName}
              onChange={e => handleChange('fullName', e.target.value)}
              required
              placeholder="As shown on government ID"
            />
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>DATE OF BIRTH</label>
            <input
              style={styles.input}
              type="date"
              value={formData.dateOfBirth}
              onChange={e => handleChange('dateOfBirth', e.target.value)}
              required
            />
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>ADDRESS</label>
            <input
              style={styles.input}
              type="text"
              value={formData.address}
              onChange={e => handleChange('address', e.target.value)}
              required
              placeholder="Street address"
            />
          </div>

          <div style={styles.row}>
            <div style={{ ...styles.inputGroup, flex: 1 }}>
              <label style={styles.label}>CITY</label>
              <input
                style={styles.input}
                type="text"
                value={formData.city}
                onChange={e => handleChange('city', e.target.value)}
                required
              />
            </div>
            <div style={{ ...styles.inputGroup, flex: 1 }}>
              <label style={styles.label}>PROVINCE/STATE</label>
              <input
                style={styles.input}
                type="text"
                value={formData.province}
                onChange={e => handleChange('province', e.target.value)}
                required
              />
            </div>
          </div>

          <div style={styles.row}>
            <div style={{ ...styles.inputGroup, flex: 1 }}>
              <label style={styles.label}>POSTAL CODE</label>
              <input
                style={styles.input}
                type="text"
                value={formData.postalCode}
                onChange={e => handleChange('postalCode', e.target.value)}
                required
              />
            </div>
            <div style={{ ...styles.inputGroup, flex: 1 }}>
              <label style={styles.label}>COUNTRY</label>
              <select
                style={styles.input}
                value={formData.country}
                onChange={e => handleChange('country', e.target.value)}
                required
              >
                <option value="Canada">Canada</option>
                <option value="United States">United States</option>
              </select>
            </div>
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>GOVERNMENT ID TYPE</label>
            <select
              style={styles.input}
              value={formData.idType}
              onChange={e => handleChange('idType', e.target.value)}
              required
            >
              <option value="">Select ID type</option>
              <option value="drivers_license">Driver's License</option>
              <option value="passport">Passport</option>
              <option value="provincial_id">Provincial/State ID</option>
            </select>
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>ID NUMBER</label>
            <input
              style={styles.input}
              type="text"
              value={formData.idNumber}
              onChange={e => handleChange('idNumber', e.target.value)}
              required
              placeholder="ID document number"
            />
          </div>

          <div style={styles.notice}>
            <p style={styles.noticeText}>
              🔒 Your information is encrypted and stored securely. We comply with all FINTRAC and AGLC
              requirements for identity verification.
            </p>
            <p style={styles.noticeText}>
              Transactions over $10,000 CAD are reported to FINTRAC as required by Canadian law.
            </p>
          </div>

          <button type="submit" style={styles.submitBtn}>
            VERIFY IDENTITY
          </button>
        </form>
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    padding: '20px',
    background: '#0a0a0a',
  },
  card: {
    maxWidth: 520,
    margin: '0 auto',
    background: '#111',
    borderRadius: 16,
    border: '1px solid #222',
    overflow: 'hidden',
  },
  header: {
    textAlign: 'center',
    padding: '24px 20px 16px',
    background: 'linear-gradient(180deg, #1a1a1a 0%, #111 100%)',
    borderBottom: '1px solid #222',
  },
  title: {
    fontSize: 18,
    fontWeight: 900,
    color: '#fff',
    letterSpacing: 2,
    margin: '8px 0 4px',
  },
  subtitle: {
    fontSize: 11,
    color: '#888',
    letterSpacing: 1,
  },
  form: {
    padding: '20px',
  },
  inputGroup: {
    marginBottom: 16,
  },
  label: {
    display: 'block',
    fontSize: 9,
    color: '#DC2626',
    fontWeight: 700,
    letterSpacing: 1,
    marginBottom: 6,
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: 8,
    border: '1px solid #333',
    background: '#0a0a0a',
    color: '#fff',
    fontSize: 13,
    boxSizing: 'border-box',
  },
  row: {
    display: 'flex',
    gap: 12,
  },
  notice: {
    padding: '12px',
    background: '#0a0a0a',
    border: '1px solid #222',
    borderRadius: 8,
    marginBottom: 16,
  },
  noticeText: {
    fontSize: 10,
    color: '#666',
    lineHeight: 1.6,
    margin: '4px 0',
  },
  submitBtn: {
    width: '100%',
    padding: '14px',
    borderRadius: 10,
    border: '2px solid #DC2626',
    background: 'linear-gradient(180deg, #DC2626 0%, #991B1B 100%)',
    color: '#fff',
    fontSize: 14,
    fontWeight: 900,
    letterSpacing: 2,
    cursor: 'pointer',
    boxShadow: '0 0 20px #DC262644',
  },
  successIcon: { fontSize: 64, textAlign: 'center', padding: '30px 0 10px' },
  successTitle: {
    fontSize: 18,
    fontWeight: 900,
    color: '#00FF88',
    textAlign: 'center',
    letterSpacing: 2,
    marginBottom: 12,
  },
  successText: {
    fontSize: 13,
    color: '#aaa',
    textAlign: 'center',
    padding: '0 30px',
    lineHeight: 1.6,
    marginBottom: 20,
  },
  complianceNote: {
    textAlign: 'center',
    padding: '16px 20px',
    background: '#0a0a0a',
    fontSize: 10,
    color: '#666',
    lineHeight: 1.6,
    borderTop: '1px solid #222',
  },
  shield: { display: 'block', fontSize: 24, marginBottom: 8 },
};
